import { useState, useCallback, useMemo, useRef } from "react";
import { exportLocalToSupabase, MIGRATION_TASKS, fixInvoiceClientIds } from "../supabaseMigration";
import { loadFromSupabase, saveToSupabase } from "../supabaseDB";
import { uploadDataUrlToStorage } from "../storageImages";

/* ═══════════════════════════════════════════════════════════════
   Supabase Migration — one-shot tool to copy every gfi-* localStorage
   record into the corresponding Supabase table. Read the
   supabaseMigration.js header for the upsert / idempotency contract.

   The UI is intentionally heavy on warnings + visible progress because:
     • This is a destination-modifying action (writes to remote DB).
     • The user will run it once, possibly months from now, possibly
       never again — it must read clearly from cold.
     • If a table errors we want the failure surface on screen, not in
       the console.

   The component is a single full-page screen; opens via the Settings
   menu entry "Migrate to Supabase" / "Migrar a Supabase".
   ═══════════════════════════════════════════════════════════════ */

const T = {
  en: {
    title: "Migrate to Supabase",
    subtitle: "One-shot tool — copy every record in this browser's localStorage to the Supabase tables. Safe to re-run (upsert on legacy id).",
    warningTitle: "Before you run this",
    w1: "The schema must already exist in Supabase. Run src/supabaseSchema.sql in the SQL editor first.",
    w2: "Attachments (PDFs, label photos, signatures) are NOT migrated in this step. Only structured data.",
    w3: "Your localStorage data is NOT deleted — it stays as a backup until you say otherwise.",
    w4: "Idempotent: re-running updates rows by legacy_id instead of duplicating.",
    runBtn: "Start migration",
    runningBtn: "Migrating…",
    confirmTitle: "Start migration?",
    confirmBody: "This will write every gfi-* localStorage record to Supabase. It usually takes a few seconds. You can re-run it safely.",
    confirmYes: "Yes, migrate",
    confirmNo: "Cancel",
    statusPending: "Pending",
    statusRunning: "Running",
    statusDone: "Done",
    statusPartial: "Partial",
    statusEmpty: "(empty)",
    summaryTitle: "Migration complete",
    summaryRecords: "records migrated",
    summaryErrors: "errors",
    summarySeconds: "seconds",
    summaryNoErrors: "All tables migrated cleanly.",
    summaryHasErrors: "Some batches failed — expand a table below to see the error message.",
    errorListTitle: "Errors on this table",
    closeBtn: "Close summary",
    notConfigured: "Supabase isn't configured for this build. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env.local and rebuild before running this.",
  },
  es: {
    title: "Migrar a Supabase",
    subtitle: "Herramienta de un solo uso — copia todos los registros de localStorage de este navegador a las tablas de Supabase. Se puede re-ejecutar (upsert por legacy id).",
    warningTitle: "Antes de ejecutar",
    w1: "El schema debe existir en Supabase. Ejecuta src/supabaseSchema.sql en el SQL editor primero.",
    w2: "Los archivos adjuntos (PDFs, fotos de etiquetas, firmas) NO se migran en este paso. Solo datos estructurados.",
    w3: "Tu data de localStorage NO se borra — queda como respaldo hasta que tú lo decidas.",
    w4: "Idempotente: re-ejecutar actualiza filas por legacy_id en vez de duplicar.",
    runBtn: "Iniciar migración",
    runningBtn: "Migrando…",
    confirmTitle: "¿Iniciar la migración?",
    confirmBody: "Esto escribe todos los registros de gfi-* a Supabase. Toma unos segundos. Se puede re-ejecutar sin problema.",
    confirmYes: "Sí, migrar",
    confirmNo: "Cancelar",
    statusPending: "Pendiente",
    statusRunning: "En curso",
    statusDone: "Completado",
    statusPartial: "Parcial",
    statusEmpty: "(vacío)",
    summaryTitle: "Migración completa",
    summaryRecords: "registros migrados",
    summaryErrors: "errores",
    summarySeconds: "segundos",
    summaryNoErrors: "Todas las tablas se migraron sin problemas.",
    summaryHasErrors: "Algunos lotes fallaron — expande una tabla abajo para ver el mensaje.",
    errorListTitle: "Errores en esta tabla",
    closeBtn: "Cerrar resumen",
    notConfigured: "Supabase no está configurado en este build. Define REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_ANON_KEY en .env.local y vuelve a buildear antes de correr esto.",
  },
};

/* Friendly labels for the table list. Falls back to the raw table name
   if the user adds a new table without updating this map. */
const TABLE_LABELS = {
  seasons: { en: "Seasons", es: "Temporadas" },
  suppliers: { en: "Suppliers / Shippers", es: "Proveedores / Exportadores" },
  clients: { en: "Clients", es: "Clientes" },
  products: { en: "Products", es: "Productos" },
  warehouses: { en: "Warehouses", es: "Bodegas" },
  brokers: { en: "Customs Brokers", es: "Agentes Aduanales" },
  growers: { en: "Growers", es: "Productores" },
  certifications: { en: "Certifications", es: "Certificaciones" },
  export_certs: { en: "Export Certificates", es: "Certificados de Exportación" },
  fee_catalog: { en: "Fee Catalog", es: "Catálogo de Gastos" },
  shipments: { en: "Shipments", es: "Embarques" },
  shipper_shipments: { en: "Shipper Portal Embarques", es: "Embarques del Portal" },
  bills: { en: "Bills", es: "Bills" },
  invoices: { en: "Invoices", es: "Facturas" },
  payments: { en: "Payments", es: "Pagos" },
  expense_bills: { en: "Expense Bills", es: "Facturas de Gastos" },
  fee_transfers: { en: "Fee Transfers", es: "Transferencias de Gastos" },
  fixed_pos: { en: "Fixed Pricing POs", es: "POs Precio Fijo" },
  consignment_pos: { en: "Consignment POs", es: "POs Consignación" },
  shipper_settings: { en: "Shipper Settings", es: "Configuración del Exportador" },
  company_profile: { en: "Company Profile", es: "Perfil de Empresa" },
};

/* Ordered list of every table the migrator touches, including the two
   special-cased ones (shipper_settings and company_profile) that
   exportLocalToSupabase runs after MIGRATION_TASKS. Keeps the UI list
   identical to the actual run order. */
const TABLE_ORDER = [
  ...MIGRATION_TASKS.map(function (t) { return t.table; }),
  "shipper_settings",
  "company_profile",
];

function fmtPct(done, total) {
  if (!total) return 0;
  return Math.min(100, Math.round((done / total) * 100));
}
function tableLabel(name, lang) {
  var L = TABLE_LABELS[name];
  return L ? L[lang] || L.en : name;
}

function StatusPill({ status, lang, t }) {
  var bg, fg;
  switch (status) {
    case "running":
      bg = "rgba(59,130,246,0.12)"; fg = "#2563EB";
      return <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: bg, color: fg }}>{t.statusRunning}</span>;
    case "done":
      bg = "rgba(16,185,129,0.12)"; fg = "#059669";
      return <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: bg, color: fg }}>{"✓ " + t.statusDone}</span>;
    case "partial":
      bg = "rgba(245,158,11,0.15)"; fg = "#B45309";
      return <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: bg, color: fg }}>{"⚠ " + t.statusPartial}</span>;
    case "empty":
      bg = "rgba(107,114,128,0.10)"; fg = "#6B7280";
      return <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 600, background: bg, color: fg, fontStyle: "italic" }}>{t.statusEmpty}</span>;
    case "pending":
    default:
      bg = "rgba(156,163,175,0.12)"; fg = "#6B7280";
      return <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 600, background: bg, color: fg }}>{t.statusPending}</span>;
  }
}

export default function SupabaseMigration({ lang: globalLang }) {
  var lang = (globalLang === "en" ? "en" : "es");
  var t = T[lang];

  /* { [tableName]: { done, total, status } } — driven by the onProgress
     callback we hand to exportLocalToSupabase. We seed it with every
     table in pending status so the list renders the full inventory
     before the run begins. */
  var initialProgress = useMemo(function () {
    var m = {};
    TABLE_ORDER.forEach(function (name) { m[name] = { done: 0, total: null, status: "pending" }; });
    return m;
  }, []);

  var [progress, setProgress] = useState(initialProgress);
  var [running, setRunning] = useState(false);
  var [summary, setSummary] = useState(null);
  var [showConfirm, setShowConfirm] = useState(false);
  var [expandedErrors, setExpandedErrors] = useState({});
  var [elapsedSec, setElapsedSec] = useState(null);
  var startedAtRef = useRef(0);

  /* Detect a misconfigured build up-front so we don't fire an HTTP
     request that'll just 401. The Supabase client logs its own warning
     but the user benefits from seeing it inline here too. */
  var notConfigured = !process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY;

  var onProgress = useCallback(function (p) {
    var status = p.status;
    /* When the run ends with `done` but total is 0, downgrade visually
       to "empty" so the user understands the table had nothing to ship
       rather than thinking we wrote zero rows by mistake. */
    if (status === "done" && (!p.total || p.total === 0)) status = "empty";
    setProgress(function (prev) {
      var next = { ...prev };
      next[p.table] = { done: p.done, total: p.total, status: status };
      return next;
    });
  }, []);

  var runMigration = useCallback(async function () {
    if (running) return;
    setShowConfirm(false);
    setRunning(true);
    setSummary(null);
    setExpandedErrors({});
    setElapsedSec(null);
    /* Reset per-table progress so a re-run starts clean visually. */
    setProgress(function () {
      var m = {};
      TABLE_ORDER.forEach(function (name) { m[name] = { done: 0, total: null, status: "pending" }; });
      return m;
    });
    startedAtRef.current = Date.now();
    try {
      var result = await exportLocalToSupabase(onProgress);
      setSummary(result);
    } catch (e) {
      /* Defensive — exportLocalToSupabase shouldn't throw, but if the
         Supabase client fails to initialize (bad env vars) it will.
         Surface the error as a fake summary so the screen isn't blank. */
      setSummary({ tables: [], totalMigrated: 0, totalErrors: 1, error: e && e.message ? e.message : String(e) });
    }
    setElapsedSec(Math.round((Date.now() - startedAtRef.current) / 1000));
    setRunning(false);
  }, [onProgress, running]);

  var totalMigrated = summary ? (summary.totalMigrated || 0) : 0;
  var totalErrors = summary ? (summary.totalErrors || 0) : 0;

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "4px 8px 40px" }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
      <p style={{ fontSize: 12, color: "var(--color-text-muted)", margin: "0 0 18px", lineHeight: 1.5 }}>{t.subtitle}</p>

      {/* Warning panel — visible always so the user reads context before
          clicking the big green button. */}
      <div style={{ padding: "14px 18px", marginBottom: 18, background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#B45309", marginBottom: 8 }}>{t.warningTitle}</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.7, color: "var(--color-text-secondary)" }}>
          <li>{t.w1}</li>
          <li>{t.w2}</li>
          <li>{t.w3}</li>
          <li>{t.w4}</li>
        </ul>
      </div>

      {notConfigured && (
        <div style={{ padding: "12px 16px", marginBottom: 18, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 8, fontSize: 12, color: "#B91C1C", fontWeight: 600 }}>
          ⚠ {t.notConfigured}
        </div>
      )}

      {/* One-shot data migration — move every base64-inlined shipment
          document into Supabase Storage. Hoisted to the top of the
          page (above the full-migration action bar) because users
          need to find it fast: LOT 58026 hit 1.5MB of inline base64
          and broke load. After this runs, shipments.documents carries
          only metadata + storagePath + url; the binary lives in
          bucket `shipment-docs`. Idempotent — safe to re-run. */}
      <div style={{ padding: "14px 18px", marginBottom: 18, borderRadius: 10, border: "2px solid rgba(220,38,38,0.45)", background: "rgba(220,38,38,0.05)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: "#B91C1C", padding: "2px 6px", borderRadius: 3, background: "rgba(220,38,38,0.15)" }}>1.5MB → 200B</span>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text)" }}>Migrate: Shipment Documents → Storage</div>
        </div>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4, lineHeight: 1.5 }}>
          Walks every shipment in Supabase and uploads any document still stored as inline base64 (legacy <code>doc.data</code>) to the <code>shipment-docs</code> bucket at <code>{"{supplierId}/{lot}/{filename}"}</code>. The row is rewritten to carry <code>{"{name,size,type,storagePath,url}"}</code> only — the inline base64 is dropped. Run once after the code deploy. Idempotent — safe to re-run.
        </div>
        <ShipmentDocsMigrateButton />
      </div>

      {/* Action bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <button
          type="button"
          onClick={function () { setShowConfirm(true); }}
          disabled={running || notConfigured}
          style={{
            padding: "11px 22px",
            borderRadius: 8,
            border: "none",
            background: running || notConfigured ? "#9ca3af" : "var(--color-accent)",
            color: "#fff",
            cursor: running || notConfigured ? "default" : "pointer",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.02em",
            opacity: running || notConfigured ? 0.6 : 1,
            boxShadow: running ? "none" : "0 2px 6px rgba(0,0,0,0.1)",
            transition: "background 0.15s, opacity 0.15s",
          }}
        >
          {running ? t.runningBtn : t.runBtn}
        </button>
        {running && (
          <span style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>
            {lang === "es" ? "No cierres esta pestaña hasta que termine." : "Don't close this tab until it finishes."}
          </span>
        )}
      </div>

      {/* Per-table progress list */}
      <div style={{ border: "1px solid var(--color-border)", borderRadius: 10, overflow: "hidden", background: "var(--color-bg-card)" }}>
        {TABLE_ORDER.map(function (name, idx) {
          var p = progress[name] || { done: 0, total: null, status: "pending" };
          var pct = fmtPct(p.done, p.total || 0);
          var sumEntry = summary ? summary.tables.find(function (x) { return x.table === name; }) : null;
          var hasErrors = sumEntry && sumEntry.errors && sumEntry.errors.length > 0;
          var expanded = !!expandedErrors[name];
          return (
            <div key={name} style={{
              padding: "10px 14px",
              borderTop: idx === 0 ? "none" : "1px solid var(--color-border)",
              background: idx % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text)" }}>{tableLabel(name, lang)}</span>
                  <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{name}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {p.total !== null && p.total > 0 && (
                    <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)", minWidth: 60, textAlign: "right" }}>
                      {p.done}/{p.total}
                    </span>
                  )}
                  <StatusPill status={p.status} lang={lang} t={t} />
                  {hasErrors && (
                    <button
                      type="button"
                      onClick={function () { setExpandedErrors(function (prev) { var n = { ...prev }; n[name] = !prev[name]; return n; }); }}
                      style={{ padding: "2px 8px", borderRadius: 5, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#B91C1C", cursor: "pointer", fontSize: 10, fontWeight: 600 }}
                    >
                      {expanded ? "▾" : "▸"} {sumEntry.errors.length}
                    </button>
                  )}
                </div>
              </div>
              {/* Live progress bar — only renders while the table is
                  running so it doesn't visually compete with the final
                  ✓ pill once done. */}
              {p.status === "running" && p.total > 0 && (
                <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: "var(--color-border)", overflow: "hidden" }}>
                  <div style={{ width: pct + "%", height: "100%", background: "var(--color-accent)", transition: "width 0.2s" }} />
                </div>
              )}
              {hasErrors && expanded && (
                <div style={{ marginTop: 8, padding: "8px 12px", background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#B91C1C", marginBottom: 4 }}>{t.errorListTitle}</div>
                  {sumEntry.errors.map(function (err, i) {
                    return (
                      <div key={i} style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "#7F1D1D", marginBottom: 3, wordBreak: "break-word" }}>
                        {err.batchStart !== undefined ? "batch@" + err.batchStart + ": " : ""}{err.message}
                        {err.details ? " — " + String(err.details) : ""}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary panel — shown after a completed run. */}
      {summary && !running && (
        <div style={{ marginTop: 22, padding: "18px 22px", borderRadius: 10, background: totalErrors === 0 ? "rgba(16,185,129,0.06)" : "rgba(245,158,11,0.06)", border: "1px solid " + (totalErrors === 0 ? "rgba(16,185,129,0.3)" : "rgba(245,158,11,0.35)") }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: totalErrors === 0 ? "#059669" : "#B45309" }}>
              {totalErrors === 0 ? "✓ " : "⚠ "}{t.summaryTitle}
            </span>
            {elapsedSec !== null && (
              <span style={{ fontSize: 11, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
                {elapsedSec} {t.summarySeconds}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 28, fontSize: 13, color: "var(--color-text-secondary)" }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)" }}>{totalMigrated}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.summaryRecords}</div>
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: totalErrors === 0 ? "var(--color-text-muted)" : "#DC2626" }}>{totalErrors}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.summaryErrors}</div>
            </div>
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
            {totalErrors === 0 ? t.summaryNoErrors : t.summaryHasErrors}
          </div>
          {summary.error && (
            <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 6, fontSize: 12, color: "#B91C1C", fontFamily: "'JetBrains Mono', monospace" }}>
              {summary.error}
            </div>
          )}
        </div>
      )}

      {/* One-shot repair tool — fix invoice.clientId on migrated
          rows. Many invoices arrived from the legacy export with
          partial names ("Four Seasons" instead of "Four Seasons
          Produce") and no clientId. This walks every invoice in
          Supabase, finds the matching client by name substring,
          and stamps both clientId + canonical clientName back. */}
      <div style={{ marginTop: 18, padding: "14px 18px", borderRadius: 10, border: "1px solid var(--color-border)", background: "var(--color-bg-card)" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text)" }}>Repair: Invoice client IDs</div>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4, lineHeight: 1.5 }}>
          Scans every invoice in Supabase and matches an unresolved or partial client name against the Clients catalog. Stamps clientId + canonical shortName on each invoice that matches. Idempotent — safe to re-run.
        </div>
        {(() => {
          /* Use refs because we don't need re-render until the run
             finishes; the button itself updates `result` state. */
          return <InvoiceClientFixButton />;
        })()}
      </div>

      {/* Confirm modal */}
      {showConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16, zIndex: 10001,
        }}>
          <div style={{ background: "var(--color-bg-card-solid, #fff)", borderRadius: 12, padding: "22px 24px", maxWidth: 440, boxShadow: "0 16px 48px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: "var(--color-text)" }}>{t.confirmTitle}</div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.6, marginBottom: 18 }}>{t.confirmBody}</div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" onClick={function () { setShowConfirm(false); }} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.confirmNo}</button>
              <button type="button" onClick={runMigration} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{t.confirmYes}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Migrate every base64-inlined document on the `shipments` table to
   Supabase Storage (bucket `shipment-docs`). Walks every shipment,
   inspects documents[], and for each entry that still carries a raw
   data URL in `data` AND has no `storagePath` yet, uploads the file
   to Storage at {supplier}/{lot}/{filename}, then rewrites the doc
   object to drop `data` and add `storagePath` + `url`.

   Idempotent: a doc that already has a storagePath (new uploads
   from the post-migration UI) is skipped. A doc with no `data`
   field is skipped (nothing to migrate). Crash-safe: each shipment
   is saved back individually, so a failure mid-loop doesn't lose
   prior progress.

   Why client-side and not pure SQL: Storage uploads require the
   anon JWT + the Storage REST API; raw SQL can't push bytes to the
   storage.objects table. */
async function migrateShipmentDocsToStorage(onProgress) {
  const out = { scanned: 0, shipmentsUpdated: 0, docsMigrated: 0, docsSkipped: 0, errors: [] };
  const rows = await loadFromSupabase("shipments", { force: true });
  const list = rows || [];
  for (let i = 0; i < list.length; i++) {
    const s = list[i];
    out.scanned++;
    if (onProgress) onProgress({ ...out, current: i + 1, total: list.length, lot: s.lot });
    const docs = Array.isArray(s.documents) ? s.documents : [];
    if (docs.length === 0) continue;
    let touched = false;
    const nextDocs = [];
    for (let j = 0; j < docs.length; j++) {
      const d = docs[j];
      if (!d) continue;
      /* Already on Storage — keep as-is. */
      if (d.storagePath) { nextDocs.push(d); out.docsSkipped++; continue; }
      /* Nothing to migrate. */
      if (typeof d.data !== "string" || !d.data.startsWith("data:")) {
        nextDocs.push(d);
        out.docsSkipped++;
        continue;
      }
      try {
        const supSeg = safeStoragePathSegment(s.supplierId || s.shipperName || s._supplierIdUuid || "no-shipper");
        const lotSeg = safeStoragePathSegment(s.lot || s.id || "no-lot");
        const fid = (d.id || Date.now() + "-" + Math.floor(Math.random() * 1000000));
        const safeName = safeStoragePathSegment(d.name || ("doc-" + fid));
        const pathBase = supSeg + "/" + lotSeg + "/" + fid + "-" + safeName.replace(/\.[a-zA-Z0-9]{1,8}$/, "");
        const up = await uploadDataUrlToStorage("shipment-docs", pathBase, d.data);
        const next = Object.assign({}, d, {
          storagePath: up.path,
          url: up.publicUrl,
          uploadedAt: d.uploadedAt || new Date().toISOString(),
        });
        delete next.data;
        nextDocs.push(next);
        out.docsMigrated++;
        touched = true;
      } catch (err) {
        out.errors.push({ lot: s.lot, name: d.name, message: (err && err.message) || String(err) });
        /* Keep the original (data URL) so we don't drop the user's
           file on a transient upload failure. They can re-run. */
        nextDocs.push(d);
      }
    }
    if (touched) {
      try {
        await saveToSupabase("shipments", Object.assign({}, s, { documents: nextDocs }));
        out.shipmentsUpdated++;
      } catch (err) {
        out.errors.push({ lot: s.lot, name: "(saveToSupabase)", message: (err && err.message) || String(err) });
      }
    }
  }
  return out;
}

/* Local copy of safeStoragePathSegment so this module doesn't pull
   in ShipmentRegistry just for a string sanitizer. Matches the
   sanitizer there byte-for-byte. */
function safeStoragePathSegment(s) {
  return String(s == null ? "" : s)
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/^_+|_+$/g, "")
    || "_";
}

function ShipmentDocsMigrateButton() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const run = async () => {
    if (busy) return;
    if (!window.confirm("This will upload every base64-inlined shipment document to Supabase Storage and rewrite the row to drop the inline data. Safe to re-run. Continue?")) return;
    setBusy(true); setError(""); setResult(null); setProgress(null);
    try {
      const r = await migrateShipmentDocsToStorage(setProgress);
      setResult(r);
    } catch (e) {
      setError(e && e.message ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: busy ? "wait" : "pointer", fontSize: 12, fontWeight: 700, opacity: busy ? 0.7 : 1 }}
        >{busy ? "Migrating…" : "Run migration"}</button>
        {progress && (
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
            {progress.current}/{progress.total} · LOT {progress.lot || "—"} · migrated {progress.docsMigrated}
          </div>
        )}
      </div>
      {result && (
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.6 }}>
          ✓ Scanned {result.scanned} shipment(s) · {result.shipmentsUpdated} updated · {result.docsMigrated} doc(s) → Storage · {result.docsSkipped} already-migrated / nothing-to-do
          {result.errors.length > 0 && (
            <div style={{ marginTop: 6, color: "#B91C1C" }}>
              ⚠ {result.errors.length} error(s):
              {result.errors.slice(0, 8).map(function (e, i) { return <div key={i} style={{ paddingLeft: 12 }}>LOT {e.lot || "—"} · {e.name || "?"} — {e.message}</div>; })}
              {result.errors.length > 8 && <div style={{ paddingLeft: 12 }}>… and {result.errors.length - 8} more</div>}
            </div>
          )}
        </div>
      )}
      {error && (
        <div style={{ fontSize: 12, color: "#B91C1C", fontFamily: "'JetBrains Mono', monospace" }}>⚠ {error}</div>
      )}
    </div>
  );
}

/* One-shot repair button for the invoice clientId migration. Local
   state — when the user clicks Run, it calls fixInvoiceClientIds()
   and shows the resulting counts. Kept as a sibling component so
   the main migration screen's state isn't muddied. */
function InvoiceClientFixButton() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const run = async () => {
    if (busy) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await fixInvoiceClientIds();
      setResult(r);
    } catch (e) {
      setError(e && e.message ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: busy ? "wait" : "pointer", fontSize: 12, fontWeight: 700, opacity: busy ? 0.7 : 1 }}
      >{busy ? "Repairing…" : "Run repair"}</button>
      {result && (
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>
          ✓ Scanned {result.totalScanned} · fixed {result.fixed} · skipped {result.skipped}
        </div>
      )}
      {error && (
        <div style={{ fontSize: 12, color: "#B91C1C", fontFamily: "'JetBrains Mono', monospace" }}>⚠ {error}</div>
      )}
    </div>
  );
}
